/**
 * Account Commitments Panel (SPRINT-6B F006)
 *
 * CRUD panel for managing account-level debt commitments.
 * Shows a table of commitment entries with add/edit/delete actions,
 * plus a totals row summarising balance, annual, and servicing totals.
 *
 * States:
 * - Empty: No commitments, shows "No existing commitments" with Add button
 * - Table: Shows commitments with columns and summary totals
 * - Modal: Add/Edit form with live annual calculation
 * - Delete: AlertDialog confirmation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineTextField } from '@/components/inline-edit/inline-text-field';
import { InlineSelectField } from '@/components/inline-edit/inline-select-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface AccountCommitmentEntry {
  id: string;
  accountId: string;
  commitmentType: string;
  lenderCreditor: string | null;
  lenderId: string | null;
  description: string | null;
  originalAmount: number | null;
  currentBalance: number;
  creditLimit: number | null;
  repaymentFrequency: string;
  repaymentAmount: number;
  annualRepayment: number;
  interestRate: number | null;
  maturityDate: string | null;
  includeInServicing: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  commitmentType: string;
  lenderCreditor: string;
  description: string;
  originalAmount: string;
  currentBalance: string;
  creditLimit: string;
  repaymentFrequency: string;
  repaymentAmount: string;
  interestRate: string;
  maturityDate: string;
  includeInServicing: boolean;
}

export interface AccountCommitmentsPanelProps {
  accountId: string;
}

// Constants
const COMMITMENT_TYPE_OPTIONS = [
  { value: 'loan', label: 'Loan' },
  { value: 'lease', label: 'Lease' },
  { value: 'hire_purchase', label: 'Hire Purchase' },
  { value: 'chattel_mortgage', label: 'Chattel Mortgage' },
  { value: 'overdraft', label: 'Overdraft' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const FREQUENCY_MULTIPLIERS: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annually: 1,
};

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const defaultFormState: FormState = {
  commitmentType: 'loan',
  lenderCreditor: '',
  description: '',
  originalAmount: '',
  currentBalance: '',
  creditLimit: '',
  repaymentFrequency: 'monthly',
  repaymentAmount: '',
  interestRate: '',
  maturityDate: '',
  includeInServicing: true,
};

export const AccountCommitmentsPanel: React.FC<AccountCommitmentsPanelProps> = ({ accountId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AccountCommitmentEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<AccountCommitmentEntry | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);

  // Fetch commitments
  const { data: entries = [], isLoading } = useQuery<AccountCommitmentEntry[]>({
    queryKey: [`/api/crm/accounts/${accountId}/commitments`],
    enabled: !!accountId,
  });

  // Live calculation of annual repayment
  const calculatedAnnual = useMemo(() => {
    const amount = parseFloat(formState.repaymentAmount) || 0;
    const multiplier = FREQUENCY_MULTIPLIERS[formState.repaymentFrequency] || 1;
    return amount * multiplier;
  }, [formState.repaymentAmount, formState.repaymentFrequency]);

  // Sync form state when editing
  useEffect(() => {
    if (editingEntry) {
      setFormState({
        commitmentType: editingEntry.commitmentType,
        lenderCreditor: editingEntry.lenderCreditor || '',
        description: editingEntry.description || '',
        originalAmount: editingEntry.originalAmount != null ? String(editingEntry.originalAmount) : '',
        currentBalance: String(editingEntry.currentBalance),
        creditLimit: editingEntry.creditLimit != null ? String(editingEntry.creditLimit) : '',
        repaymentFrequency: editingEntry.repaymentFrequency,
        repaymentAmount: String(editingEntry.repaymentAmount),
        interestRate: editingEntry.interestRate != null ? String(editingEntry.interestRate) : '',
        maturityDate: editingEntry.maturityDate || '',
        includeInServicing: editingEntry.includeInServicing,
      });
    }
  }, [editingEntry]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/crm/accounts/${accountId}/commitments`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/accounts/${accountId}/commitments`] });
      handleCloseModal();
      toast({ title: 'Commitment added', description: 'Commitment has been created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create commitment', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `/api/crm/accounts/${accountId}/commitments/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/accounts/${accountId}/commitments`] });
      if (isModalOpen) {
        handleCloseModal();
      }
      toast({ title: 'Commitment updated', description: 'Commitment has been updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update commitment', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/crm/accounts/${accountId}/commitments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/accounts/${accountId}/commitments`] });
      setDeleteEntry(null);
      toast({ title: 'Commitment deleted', description: 'Commitment has been removed' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete commitment', variant: 'destructive' });
    },
  });

  // Toggle servicing mutation
  const toggleServicingMutation = useMutation({
    mutationFn: async ({ id, includeInServicing }: { id: string; includeInServicing: boolean }) => {
      const res = await apiRequest('PATCH', `/api/crm/accounts/${accountId}/commitments/${id}`, { includeInServicing });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/accounts/${accountId}/commitments`] });
      toast({ title: 'Commitment servicing updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update servicing', variant: 'destructive' });
    },
  });

  // Inline edit handler — updates a single field
  const handleInlineUpdate = async (entryId: string, field: string, value: string) => {
    const payload: Record<string, any> = {};
    if (field === 'currentBalance' || field === 'repaymentAmount') {
      const trimmed = value.trim();
      if (trimmed === '') return; // reject empty — don't silently save $0
      const parsed = parseFloat(trimmed);
      if (isNaN(parsed)) return;
      payload[field] = parsed;
    } else {
      payload[field] = value || null;
    }
    await updateMutation.mutateAsync({ id: entryId, data: payload });
  };

  const handleOpenAdd = () => {
    setEditingEntry(null);
    setFormState(defaultFormState);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (entry: AccountCommitmentEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setFormState(defaultFormState);
  };

  const handleSave = async () => {
    const payload: Record<string, any> = {
      commitmentType: formState.commitmentType,
      lenderCreditor: formState.lenderCreditor || null,
      description: formState.description || null,
      currentBalance: parseFloat(formState.currentBalance),
      repaymentFrequency: formState.repaymentFrequency,
      repaymentAmount: parseFloat(formState.repaymentAmount),
      includeInServicing: formState.includeInServicing,
    };

    if (formState.originalAmount) payload.originalAmount = parseFloat(formState.originalAmount);
    if (formState.creditLimit) payload.creditLimit = parseFloat(formState.creditLimit);
    if (formState.interestRate) payload.interestRate = parseFloat(formState.interestRate);
    if (formState.maturityDate) payload.maturityDate = formState.maturityDate;

    if (editingEntry) {
      await updateMutation.mutateAsync({ id: editingEntry.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalBalance = entries.reduce((sum, e) => sum + Number(e.currentBalance), 0);
    const totalAnnual = entries.reduce((sum, e) => sum + Number(e.annualRepayment), 0);
    const included = entries.filter(e => e.includeInServicing);
    const servicingTotal = included.reduce((sum, e) => sum + Number(e.annualRepayment), 0);
    return { totalBalance, totalAnnual, servicingTotal };
  }, [entries]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="account-commitments-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="account-commitments-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Existing Commitments</CardTitle>
            <Button variant="outline" size="sm" onClick={handleOpenAdd} data-testid="add-commitment-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Commitment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CreditCard className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">No existing commitments</p>
              <p className="text-xs text-muted-foreground mb-6">
                Track existing debt commitments that feed into serviceability calculations
              </p>
              <Button onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Commitment
              </Button>
            </div>
          ) : (
            // Table view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Lender</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Balance</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Repayment</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Frequency</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Annual</th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground">Servicing</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-1 px-1">
                        <InlineSelectField
                          value={entry.commitmentType}
                          options={COMMITMENT_TYPE_OPTIONS}
                          onSave={(value) => handleInlineUpdate(entry.id, 'commitmentType', value)}
                          testId="commitment-type-select"
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="py-1 px-1">
                        <InlineTextField
                          value={entry.lenderCreditor}
                          onSave={(value) => handleInlineUpdate(entry.id, 'lenderCreditor', value)}
                          placeholder="-"
                          testId="commitment-lender"
                          className="text-sm"
                        />
                      </td>
                      <td className="py-1 px-1 text-right">
                        <InlineTextField
                          value={entry.currentBalance != null ? String(entry.currentBalance) : ''}
                          onSave={(value) => handleInlineUpdate(entry.id, 'currentBalance', value)}
                          placeholder="$0"
                          testId="commitment-balance"
                          formatDisplay={(v) => formatCurrency(Number(v))}
                          className="text-sm text-right"
                        />
                      </td>
                      <td className="py-1 px-1 text-right">
                        <InlineTextField
                          value={entry.repaymentAmount != null ? String(entry.repaymentAmount) : ''}
                          onSave={(value) => handleInlineUpdate(entry.id, 'repaymentAmount', value)}
                          placeholder="$0"
                          testId="commitment-repayment"
                          formatDisplay={(v) => formatCurrency(Number(v))}
                          className="text-sm text-right"
                        />
                      </td>
                      <td className="py-1 px-1">
                        <InlineSelectField
                          value={entry.repaymentFrequency}
                          options={FREQUENCY_OPTIONS}
                          onSave={(value) => handleInlineUpdate(entry.id, 'repaymentFrequency', value)}
                          testId="commitment-frequency-select"
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.annualRepayment))}</td>
                      <td className="py-2 px-2 text-center">
                        <Switch
                          checked={entry.includeInServicing}
                          onCheckedChange={(checked) =>
                            toggleServicingMutation.mutate({ id: entry.id, includeInServicing: checked })
                          }
                          aria-label="toggle servicing"
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(entry)}
                            aria-label="edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteEntry(entry)}
                            aria-label="delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold" data-testid="account-commitments-totals">
                    <td className="py-2 px-2" colSpan={2}>Totals</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(totals.totalBalance)}</td>
                    <td className="py-2 px-2"></td>
                    <td className="py-2 px-2"></td>
                    <td className="py-2 px-2 text-right">{formatCurrency(totals.totalAnnual)}</td>
                    <td className="py-2 px-2 text-center text-xs">{formatCurrency(totals.servicingTotal)}</td>
                    <td className="py-2 px-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Commitment' : 'Add Commitment'}</DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the commitment details below' : 'Enter commitment details for this account'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Commitment Type */}
            <div className="space-y-2">
              <Label htmlFor="commitmentType">Commitment Type</Label>
              <select
                id="commitmentType"
                name="commitmentType"
                data-testid="commitment-type-select"
                value={formState.commitmentType}
                onChange={(e) => setFormState(prev => ({ ...prev, commitmentType: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {COMMITMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Lender/Creditor */}
            <div className="space-y-2">
              <Label htmlFor="lenderCreditor">Lender / Creditor</Label>
              <Input
                id="lenderCreditor"
                name="lenderCreditor"
                value={formState.lenderCreditor}
                onChange={(e) => setFormState(prev => ({ ...prev, lenderCreditor: e.target.value }))}
                placeholder="e.g., ANZ Bank"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                name="description"
                value={formState.description}
                onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Commercial Term Loan"
              />
            </div>

            {/* Original Amount */}
            <div className="space-y-2">
              <Label htmlFor="originalAmount">Original Amount ($)</Label>
              <Input
                id="originalAmount"
                name="originalAmount"
                type="number"
                step="0.01"
                min="0"
                value={formState.originalAmount}
                onChange={(e) => setFormState(prev => ({ ...prev, originalAmount: e.target.value }))}
                placeholder="e.g., 600000"
              />
            </div>

            {/* Current Balance */}
            <div className="space-y-2">
              <Label htmlFor="currentBalance">Current Balance ($)</Label>
              <Input
                id="currentBalance"
                name="currentBalance"
                type="number"
                step="0.01"
                min="0"
                value={formState.currentBalance}
                onChange={(e) => setFormState(prev => ({ ...prev, currentBalance: e.target.value }))}
                placeholder="e.g., 500000"
              />
            </div>

            {/* Credit Limit */}
            <div className="space-y-2">
              <Label htmlFor="creditLimit">Credit Limit ($)</Label>
              <Input
                id="creditLimit"
                name="creditLimit"
                type="number"
                step="0.01"
                min="0"
                value={formState.creditLimit}
                onChange={(e) => setFormState(prev => ({ ...prev, creditLimit: e.target.value }))}
                placeholder="For revolving facilities"
              />
            </div>

            {/* Repayment Frequency */}
            <div className="space-y-2">
              <Label htmlFor="repaymentFrequency">Repayment Frequency</Label>
              <select
                id="repaymentFrequency"
                name="repaymentFrequency"
                data-testid="repayment-frequency-select"
                value={formState.repaymentFrequency}
                onChange={(e) => setFormState(prev => ({ ...prev, repaymentFrequency: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Repayment Amount */}
            <div className="space-y-2">
              <Label htmlFor="repaymentAmount">Repayment Amount ($)</Label>
              <Input
                id="repaymentAmount"
                name="repaymentAmount"
                type="number"
                step="0.01"
                min="0"
                value={formState.repaymentAmount}
                onChange={(e) => setFormState(prev => ({ ...prev, repaymentAmount: e.target.value }))}
                placeholder="e.g., 4500"
              />
            </div>

            {/* Annual Repayment (read-only, auto-calculated) */}
            <div className="space-y-2">
              <Label>Annual Repayment</Label>
              <div
                data-testid="annual-repayment-display"
                className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm font-medium"
              >
                {formatCurrency(calculatedAnnual)}
              </div>
            </div>

            {/* Interest Rate */}
            <div className="space-y-2">
              <Label htmlFor="interestRate">Interest Rate (%)</Label>
              <Input
                id="interestRate"
                name="interestRate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formState.interestRate}
                onChange={(e) => setFormState(prev => ({ ...prev, interestRate: e.target.value }))}
                placeholder="e.g., 6.5"
              />
            </div>

            {/* Maturity Date */}
            <div className="space-y-2">
              <Label htmlFor="maturityDate">Maturity Date</Label>
              <Input
                id="maturityDate"
                name="maturityDate"
                type="date"
                value={formState.maturityDate}
                onChange={(e) => setFormState(prev => ({ ...prev, maturityDate: e.target.value }))}
              />
            </div>

            {/* Include in Servicing toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="includeInServicing">Include in Servicing</Label>
              <Switch
                id="includeInServicing"
                checked={formState.includeInServicing}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, includeInServicing: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingEntry ? (
                'Save Changes'
              ) : (
                'Add Commitment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Commitment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteEntry?.description || deleteEntry?.commitmentType} commitment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntry && deleteMutation.mutate(deleteEntry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AccountCommitmentsPanel;
